import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { trpc } from "@/lib/trpc";
import { authedFetch } from "@/lib/auth";
import { readAISDKStream, isDataChunk } from "@/lib/ai-sdk-reader";
import { Page } from "@/components/layout/page";
import { PromptInput } from "@/components/ui/prompt-input";
import { CandidateCard } from "@/components/discovery/candidate-card";
import {
  AddSourceDialog,
  type AddSourceInitialValues,
} from "@/components/add-source-dialog";
import { toast } from "sonner";
import type { DiscoveryCandidate } from "@iamu/shared";
import {
  Search,
  Loader2,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
} from "lucide-react";

// ─── Types ────────────────────────────────────────────────────────────

interface DiscoverySearchQuery {
  query: string;
  hierarchyTarget: string;
}

type Phase =
  | "idle"
  | "generating_queries"
  | "searching"
  | "evaluating"
  | "done";

// ─── Main Page ────────────────────────────────────────────────────────

export function ProjectDiscoverPage() {
  const { id: projectId } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const _utils = trpc.useUtils();

  // Discovery state
  const [description, setDescription] = useState("");
  const [phase, setPhase] = useState<Phase>("idle");
  const [candidates, setCandidates] = useState<DiscoveryCandidate[]>([]);
  const [queries, setQueries] = useState<DiscoverySearchQuery[]>([]);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [showQueries, setShowQueries] = useState(false);
  const [submittedDescription, setSubmittedDescription] = useState<
    string | null
  >(null);
  const [errors, setErrors] = useState<string[]>([]);

  // Ingestion state — track ingested URLs and their sourceIds
  const [ingestedUrls, setIngestedUrls] = useState<Map<string, string>>(
    new Map(),
  );

  // Add source dialog state (opened when user clicks "Add" on a candidate)
  const [addSourceValues, setAddSourceValues] = useState<
    AddSourceInitialValues | undefined
  >();
  const [addSourceOpen, setAddSourceOpen] = useState(false);
  const [pendingIngestUrl, setPendingIngestUrl] = useState<string | null>(null);

  // Refs
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  const isStreaming = phase !== "idle" && phase !== "done";
  const prefill = searchParams.get("prefill");
  const restoredRef = useRef(false);

  // Restore previous session from URL param
  const urlSessionId = searchParams.get("sessionId");
  const { data: restoredSession } = trpc.discovery.getSession.useQuery(
    { id: urlSessionId! },
    {
      enabled:
        !!urlSessionId &&
        !restoredRef.current &&
        phase === "idle" &&
        candidates.length === 0,
    },
  );

  useEffect(() => {
    if (!restoredSession || restoredRef.current) return;
    restoredRef.current = true;

    const restored = (restoredSession.candidates ?? []) as DiscoveryCandidate[];
    if (restored.length > 0 || restoredSession.description) {
      setCandidates(restored);
      setSessionId(restoredSession.id);
      setSubmittedDescription(restoredSession.description);
      setPhase("done");

      // Mark already-ingested URLs (we don't have sourceId mapping for these)
      const sourceIds = restoredSession.sourceIds ?? [];
      if (sourceIds.length > 0) {
        const ingestedMap = new Map<string, string>();
        for (const c of restored) {
          if (c.deduplication.type === "exact_url") {
            ingestedMap.set(c.url, c.deduplication.existingSourceId);
          }
        }
        setIngestedUrls(ingestedMap);
      }
    }
  }, [restoredSession]);

  // Abort SSE stream on unmount
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    if (!prefill || description.trim() || submittedDescription) return;
    setDescription(prefill);
  }, [description, prefill, submittedDescription]);

  // Auto-scroll as candidates arrive
  useEffect(() => {
    if (scrollRef.current && isStreaming) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [candidates.length, isStreaming]);

  // Process AI SDK v6 stream chunks
  const processChunk = (chunk: import("@/lib/ai-sdk-reader").AISDKChunk) => {
    if (chunk.type === "error") {
      const msg =
        "errorText" in chunk
          ? String(chunk.errorText)
          : "An unknown error occurred";
      setErrors((prev) => [...prev, msg]);
      toast.error(msg);
      return;
    }

    if (!isDataChunk(chunk)) return;

    const { data } = chunk;
    switch (chunk.type) {
      case "data-session_id": {
        const sid = (data as { sessionId: string }).sessionId;
        setSessionId(sid);
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.set("sessionId", sid);
            next.delete("prefill");
            return next;
          },
          { replace: true },
        );
        break;
      }
      case "data-phase_change": {
        const phase = (data as { phase: string }).phase;
        setPhase(phase === "done" ? "done" : (phase as Phase));
        break;
      }
      case "data-queries_generated":
        setQueries((data as { queries: DiscoverySearchQuery[] }).queries);
        break;
      case "data-candidate":
        setCandidates((prev) => [
          ...prev,
          (data as { candidate: DiscoveryCandidate }).candidate,
        ]);
        break;
      case "data-done":
        setPhase("done");
        break;
      case "data-error": {
        const msg =
          (data as { message?: string }).message || "An unknown error occurred";
        setErrors((prev) => [...prev, msg]);
        toast.error(msg);
        break;
      }
    }
  };

  const handleSubmit = useCallback(async () => {
    if (!description.trim() || isStreaming) return;

    const desc = description.trim();
    setSubmittedDescription(desc);
    setDescription("");
    setPhase("generating_queries");
    setCandidates([]);
    setQueries([]);
    setErrors([]);
    setShowQueries(false);

    // Abort any previous stream (discovery + ingestion tracking)
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const response = await authedFetch("/api/discover/stream", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          description: desc,
          projectId,
        }),
        signal: controller.signal,
      });

      if (!response.ok) {
        const err = await response
          .json()
          .catch(() => ({ error: "Discovery failed" }));
        toast.error(err.error || err.detail || "Discovery failed");
        setPhase("idle");
        return;
      }

      const reader = response.body?.getReader();
      if (!reader) {
        toast.error("No response stream");
        setPhase("idle");
        return;
      }

      await readAISDKStream(reader, (chunk) => {
        processChunk(chunk);
      });

      // Safety net: ensure phase is "done" when stream ends normally
      setPhase("done");
    } catch (err) {
      if ((err as Error).name !== "AbortError") {
        toast.error("Discovery stream disconnected");
      }
      setPhase("done");
    }
  }, [description, isStreaming, projectId]);

  const handleChipSelect = (text: string) => {
    setDescription(text);
    textareaRef.current?.focus();
  };

  const handleCandidateIngest = (candidate: DiscoveryCandidate) => {
    // Map discovery ingest strategy to crawl settings
    const maxDepth =
      candidate.ingestStrategy === "full_crawl" ||
      candidate.ingestStrategy === "filtered_pages"
        ? 1
        : 0;
    const maxPages =
      candidate.ingestStrategy === "full_crawl" ||
      candidate.ingestStrategy === "filtered_pages"
        ? 50
        : 8;

    setPendingIngestUrl(candidate.url);
    setAddSourceValues({
      url: candidate.url,
      name: candidate.suggestedName,
      authorityLevel: candidate.suggestedAuthorityLevel,
      language: candidate.language ?? undefined,
      maxDepth,
      maxPages,
    });
    setAddSourceOpen(true);
  };

  const recordIngested = trpc.discovery.recordIngested.useMutation();

  const handleAddSourceSuccess = (sourceId: string) => {
    if (pendingIngestUrl) {
      setIngestedUrls(
        (prev) => new Map([...prev, [pendingIngestUrl, sourceId]]),
      );
      setPendingIngestUrl(null);

      // Record in discovery session for persistence across page reloads
      if (sessionId && projectId) {
        recordIngested.mutate({ sessionId, projectId, sourceId });
      }
    }
  };

  const hasContent = submittedDescription || candidates.length > 0;

  return (
    <Page
      title="Discover sources"
      description="Describe your domain and let AI find authoritative sources for your knowledge base."
      back={
        projectId
          ? { to: `/projects/${projectId}/sources`, label: "Sources" }
          : undefined
      }
    >
      <div className="flex flex-col gap-6">
        {/* Submitted description bubble */}
        {submittedDescription && (
          <div className="flex justify-end">
            <div className="max-w-lg rounded-2xl rounded-tr-md bg-primary px-4 py-2.5 text-sm text-primary-foreground">
              {submittedDescription}
            </div>
          </div>
        )}

        {/* Phase indicator */}
        {isStreaming && <PhaseIndicator phase={phase} />}

        {/* Search queries (collapsible) */}
        {queries.length > 0 && (
          <div>
            <button
              type="button"
              onClick={() => setShowQueries(!showQueries)}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              {showQueries ? (
                <ChevronDown className="h-3 w-3" />
              ) : (
                <ChevronRight className="h-3 w-3" />
              )}
              {queries.length} search queries generated
            </button>
            {showQueries && (
              <div className="mt-2 space-y-1.5 pl-4">
                {queries.map((q, i) => (
                  <div key={i} className="flex items-start gap-2 text-xs">
                    <Search className="mt-0.5 h-3 w-3 shrink-0 text-muted-foreground" />
                    <div>
                      <span className="text-foreground">{q.query}</span>
                      <span className="ml-2 text-muted-foreground">
                        ({q.hierarchyTarget})
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Candidates list */}
        {candidates.length > 0 && (
          <div className="space-y-2">
            <p className="text-sm font-medium text-muted-foreground">
              {phase === "done"
                ? `Found ${candidates.length} source${candidates.length !== 1 ? "s" : ""}`
                : `Finding sources... (${candidates.length} so far)`}
            </p>
            <div className="space-y-2">
              {candidates.map((candidate, i) => (
                <CandidateCard
                  key={candidate.url}
                  candidate={candidate}
                  index={i}
                  isIngested={ingestedUrls.has(candidate.url)}
                  onIngest={() => handleCandidateIngest(candidate)}
                  projectId={projectId}
                  sourceId={ingestedUrls.get(candidate.url)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Error summary when discovery finished with errors */}
        {phase === "done" && errors.length > 0 && candidates.length === 0 && (
          <div className="rounded-lg border border-destructive/50 bg-destructive/5 p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
              <div className="space-y-1.5">
                <p className="text-sm font-medium">
                  Discovery encountered errors
                </p>
                <ul className="space-y-1 text-sm text-muted-foreground">
                  {errors.map((err, i) => (
                    <li key={i}>{err}</li>
                  ))}
                </ul>
                <p className="text-xs text-muted-foreground">
                  Try again, or check that external services (Brave Search, LLM)
                  are configured and reachable.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Partial error notice when some queries failed but we got results */}
        {phase === "done" && errors.length > 0 && candidates.length > 0 && (
          <div className="rounded-lg border border-mark-important/30 bg-mark-important/5 p-3">
            <div className="flex items-start gap-2">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-severity-important" />
              <p className="text-xs text-muted-foreground">
                {errors.length} search
                {errors.length !== 1 ? " queries" : " query"} failed. Results
                shown are from the queries that succeeded.
              </p>
            </div>
          </div>
        )}

        {/* Empty state (no submission yet) */}
        {!hasContent && phase === "idle" && (
          <EmptyState onChipSelect={handleChipSelect} projectId={projectId} />
        )}
      </div>

      {/* Add source dialog (reused from regular add-source flow) */}
      <AddSourceDialog
        open={addSourceOpen}
        onOpenChange={setAddSourceOpen}
        initialValues={addSourceValues}
        onSuccess={handleAddSourceSuccess}
      />

      {/* Bottom input (always visible) */}
      <PromptInput
        className="mt-8 shrink-0 border-t bg-background px-0 pb-3 pt-2.5"
        value={description}
        onValueChange={setDescription}
        onSubmit={handleSubmit}
        textareaRef={textareaRef}
        placeholder={
          hasContent
            ? "Describe another domain to discover more sources..."
            : "Describe your domain to discover authoritative sources..."
        }
        disabled={isStreaming}
        isLoading={isStreaming}
      />
    </Page>
  );
}

// ─── Empty State ──────────────────────────────────────────────────────

const FALLBACK_CHIPS = [
  "Swedish food safety regulations",
  "GDPR compliance for healthcare",
  "EU AI Act and machine learning standards",
  "Building construction codes in Sweden",
];

function EmptyState({
  onChipSelect,
  projectId,
}: {
  onChipSelect: (text: string) => void;
  projectId?: string;
}) {
  const { data: project } = trpc.project.getById.useQuery(
    { id: projectId ?? "" },
    { enabled: !!projectId },
  );

  const ctx = project?.context as
    | { discoverySuggestions?: string[] }
    | null
    | undefined;
  const chips = ctx?.discoverySuggestions?.length
    ? ctx.discoverySuggestions
    : FALLBACK_CHIPS;

  return (
    <div className="flex flex-col items-center gap-6 py-16 text-center">
      <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10">
        <Search className="h-8 w-8 text-primary" />
      </div>
      <div>
        <h2 className="text-lg font-semibold">
          Discover authoritative sources
        </h2>
        <p className="mt-1 max-w-md text-sm text-muted-foreground">
          Describe your domain and Iamu will search for legislation,
          regulations, standards, and guidelines — then help you ingest the best
          ones.
        </p>
      </div>
      <div className="flex flex-wrap justify-center gap-2">
        {chips.map((chip) => (
          <button
            key={chip}
            type="button"
            onClick={() => onChipSelect(chip)}
            className="rounded-full border bg-muted/50 px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            {chip}
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Phase Indicator ──────────────────────────────────────────────────

const PHASE_LABELS: Record<string, string> = {
  generating_queries: "Generating search queries...",
  searching: "Searching the web...",
  evaluating: "Evaluating results...",
};

function PhaseIndicator({ phase }: { phase: Phase }) {
  const label = PHASE_LABELS[phase];
  if (!label) return null;

  return (
    <div className="flex items-center gap-2 rounded-lg border bg-muted/30 px-3 py-2">
      <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
      <span className="text-sm text-muted-foreground">{label}</span>
    </div>
  );
}
